import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import NasStatusPill from '../components/NasStatusPill';

interface AuditEvent {
  userId: string;
  action: string;
  timestamp: string;
  ip: string;
  recordingId?: string;
  systemId?: string;
}

interface OpsData {
  timestamp: string;
  totalRecordings: number;
  recentAudit: AuditEvent[];
  backupVerification: { date?: string; status?: string; checked?: number; passed?: number } | null;
}

function Operations() {
  const [ops, setOps] = useState<OpsData | null>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditDate, setAuditDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getOpsStatus().catch(() => null),
      api.getAuditLog(auditDate).catch(() => ({ events: [] })),
    ]).then(([opsData, auditData]) => {
      setOps(opsData);
      setAuditEvents(auditData?.events || []);
    }).finally(() => setLoading(false));
  }, [auditDate]);

  if (loading) return <div className="loading">Loading operations data...</div>;

  const bv = ops?.backupVerification;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>System Status</h1>
            <p>Operational health, audit trail, and backup verification</p>
          </div>
          <a href="/status" target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#00bcd4', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 500, textDecoration: 'none', whiteSpace: 'nowrap', marginTop: 4 }}>
            Public Status Page &#8599;
          </a>
        </div>
      </div>

      {/* Status cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>NAS</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <NasStatusPill />
          </div>
        </div>
        <div className="stat-card">
          <h3>Total Recordings</h3>
          <div className="value">{ops?.totalRecordings || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Last Backup Check</h3>
          <div style={{ fontSize: '14px', color: '#555' }}>
            {bv ? (
              <>
                <div><span className={`badge ${bv.status === 'pass' ? 'badge-success' : 'badge-danger'}`}>{bv.status === 'pass' ? 'Passed' : 'Failed'}</span></div>
                <div style={{ marginTop: '4px' }}>{bv.checked} checked, {bv.passed} passed</div>
                <div style={{ color: '#999', fontSize: '12px' }}>{bv.date}</div>
              </>
            ) : (
              <span style={{ color: '#999' }}>No verification yet</span>
            )}
          </div>
        </div>
        <div className="stat-card">
          <h3>Audit Events Today</h3>
          <div className="value">{auditDate === new Date().toISOString().split('T')[0] ? auditEvents.length : '-'}</div>
        </div>
      </div>

      {/* Audit Trail */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
        <h2>Audit Trail</h2>
        <input
          type="date"
          value={auditDate}
          onChange={e => setAuditDate(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
        />
      </div>

      {auditEvents.length === 0 ? (
        <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
          <p>No audit events for {auditDate}.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Action</th>
              <th>Details</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {auditEvents.map((evt, i) => (
              <tr key={i}>
                <td>{evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString() : '-'}</td>
                <td>{evt.userId}</td>
                <td><span className="badge badge-info">{evt.action}</span></td>
                <td style={{ fontSize: '12px', color: '#666' }}>
                  {evt.recordingId && `Recording: ${evt.recordingId}`}
                  {evt.systemId && ` System: ${evt.systemId}`}
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{evt.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* System Info */}
      <div className="setup-card" style={{ marginTop: '24px' }}>
        <h3>System Information</h3>
        <table style={{ fontSize: '14px', lineHeight: '2' }}>
          <tbody>
            <tr><td style={{ fontWeight: 600, paddingRight: '20px' }}>Dashboard</td><td>recordings.woxomhealth.com</td></tr>
            <tr><td style={{ fontWeight: 600, paddingRight: '20px' }}>AWS Account</td><td>031648257673 (woxom)</td></tr>
            <tr><td style={{ fontWeight: 600, paddingRight: '20px' }}>NAS IP</td><td>107.195.29.153 (SFTP port 2222)</td></tr>
            <tr><td style={{ fontWeight: 600, paddingRight: '20px' }}>NAS Stream</td><td>nas-stream.woxomhealth.com:8443</td></tr>
            <tr><td style={{ fontWeight: 600, paddingRight: '20px' }}>S3 Bucket</td><td>nas-recordings-audio-031648257673</td></tr>
            <tr><td style={{ fontWeight: 600, paddingRight: '20px' }}>Ingestion</td><td>Every 5 minutes (cron on NAS)</td></tr>
            <tr><td style={{ fontWeight: 600, paddingRight: '20px' }}>S3 Lifecycle</td><td>Standard 30d → Standard-IA → Deep Archive 120d</td></tr>
            <tr><td style={{ fontWeight: 600, paddingRight: '20px' }}>NAS Retention</td><td>3 years, then Glacier only</td></tr>
            <tr><td style={{ fontWeight: 600, paddingRight: '20px' }}>Total Retention</td><td>10 years (Medicare compliance)</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Operations;
